import { FC } from 'react';
import {
  Box,
  HeadingText,
  ParagraphText,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { AddressContent } from 'models/profile-settings.model';
import { EditableDetailItem } from '../editable-detail-item.component';

interface AddressEditSectionProps {
  address: AddressContent;
  editValues: Record<string, string>;
  onChange: (id: string, value: string) => void;
  isMobile: boolean;
  getFieldError?: (fieldId: string) => string;
  hasAnyAddressChanges?: boolean;
}

export const AddressEditSection: FC<AddressEditSectionProps> = ({
  address,
  editValues,
  onChange,
  isMobile,
  getFieldError,
  hasAnyAddressChanges = false,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Box css={{ width: '100%', marginBottom: '$two' }}>
      <HeadingText
        level="4"
        size="xs"
        css={{ fontSize: '18px', margin: '$one 0' }}
      >
        {formatMessage({ id: address.title as never })}
      </HeadingText>
      <ParagraphText
        css={{
          fontSize: '$label',
          paddingBottom: '$one',
        }}
      >
        {formatMessage({ id: address.description as never })}
      </ParagraphText>
      <StackLayout
        css={{
          display: 'grid',
          gridTemplateColumns: isMobile
            ? '1fr'
            : 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: isMobile ? '$two' : '$one',
          width: '100%',
        }}
      >
        {address.contents.map((content) => {
          const fieldId = `${content.labelId}_${address.type}`;
          const isRequiredField =
            hasAnyAddressChanges &&
            [
              'STR_STREET_ADDRESS',
              'STR_CITY',
              'STR_STATE',
              'STR_POSTAL_CODE',
            ].includes(content.labelId);

          return (
            <EditableDetailItem
              key={content.labelId}
              id={fieldId}
              labelId={content.labelId}
              options={content.options}
              value={editValues[fieldId] ?? content.value}
              type={content.type}
              onChange={onChange}
              externalError={getFieldError?.(fieldId)}
              isRequired={isRequiredField}
            />
          );
        })}
      </StackLayout>
    </Box>
  );
};
